import { Alert, Button, Form, Input } from "antd";
import { useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../app-context";
import { BrandLogo } from "../components/BrandLogo";
import { SystemVersion } from "../components/SystemVersion";
import { useToast } from "../components/Toast";
import { LanguageMenu } from "../hooks/useLanguage";
import { ThemeToggle, useTheme } from "../hooks/useTheme";
import { api, userErrorMessage } from "../shared-core";

interface AuthCardProps {
  title: string;
  hint: string;
  error: string;
  onFinish: (values: Record<string, string>) => Promise<void>;
  submitLabel: string;
  submitting: boolean;
  children: ReactNode;
}

function AuthCard({ title, hint, error, onFinish, submitLabel, submitting, children }: AuthCardProps) {
  const { t } = useTranslation();
  const { theme, toggle } = useTheme();
  return (
    <main className="login-view">
      <div className="login-toolbar">
        <LanguageMenu />
        <ThemeToggle theme={theme} onToggle={toggle} />
      </div>
      <section className="login-card" aria-labelledby="auth-title">
        <div className="brand">
          <BrandLogo className="login-brand-logo" label={t("common.productName")} />
          <div className="brand-context">
            <strong>eCoC</strong>
            <small>{t("common.softwareSubtitle")} · {t("common.softwareVersion")}</small>
          </div>
        </div>
        <h1 id="auth-title">{title}</h1>
        <p className="hint">{hint}</p>
        {error ? <Alert type="error" showIcon message={error} role="alert" /> : null}
        <Form layout="vertical" requiredMark={false} onFinish={onFinish} autoComplete="off">
          {children}
          <Form.Item className="auth-submit">
            <Button type="primary" htmlType="submit" loading={submitting} block size="large">
              {submitLabel}
            </Button>
          </Form.Item>
        </Form>
        <SystemVersion />
      </section>
    </main>
  );
}

export function SetupView() {
  const { t } = useTranslation();
  const { completeSetup } = useApp();
  const { toast } = useToast();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values: Record<string, string>) => {
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const result = await api("/api/auth/setup", {
        json: { displayName: values.displayName?.trim(), login: values.login?.trim(), password: values.password },
      });
      completeSetup(result.user);
      toast(t("auth.setupComplete"));
    } catch (setupError: any) {
      const details = setupError?.fieldErrors && typeof setupError.fieldErrors === "object"
        ? Object.values(setupError.fieldErrors).filter(Boolean)
        : [];
      setError([...new Set([setupError.message, ...details])].join("；") as string);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title={t("auth.setupTitle")}
      hint={t("auth.setupHint")}
      error={error}
      onFinish={onFinish}
      submitLabel={t("auth.finishSetup")}
      submitting={submitting}
    >
      <Form.Item name="displayName" label={t("auth.displayName")} rules={[{ required: true, message: t("auth.completeAdmin") }]}>
        <Input autoComplete="name" />
      </Form.Item>
      <Form.Item name="login" label={t("auth.loginName")} rules={[{ required: true, message: t("auth.completeAdmin") }]}>
        <Input autoComplete="username" />
      </Form.Item>
      <Form.Item name="password" label={t("auth.password")} rules={[{ required: true }, { min: 8, message: t("auth.passwordMin") }]}>
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <Form.Item
        name="confirmPassword"
        label={t("auth.confirmPassword")}
        dependencies={["password"]}
        rules={[
          { required: true },
          ({ getFieldValue }) => ({
            validator: (_, value) => !value || value === getFieldValue("password")
              ? Promise.resolve()
              : Promise.reject(new Error(t("auth.passwordMismatch"))),
          }),
        ]}
      >
        <Input.Password autoComplete="new-password" />
      </Form.Item>
    </AuthCard>
  );
}

export function LoginView() {
  const { t } = useTranslation();
  const { completeLogin } = useApp();
  const { toast } = useToast();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const roleName = (role: string) => role === "ADMIN"
    ? t("shell.roleAdmin")
    : role === "OPERATOR"
      ? t("shell.roleOperator")
      : t("shell.roleViewer");

  const onFinish = async (values: Record<string, string>) => {
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const result = await api("/api/auth/login", { json: { login: values.login?.trim(), password: values.password } });
      completeLogin(result.user);
      if (!result.user.mustChangePassword) {
        toast(t("auth.welcome", { name: result.user.displayName, role: roleName(result.user.role) }));
      }
    } catch (loginError) {
      setError(userErrorMessage(loginError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title={t("auth.loginTitle")}
      hint={t("auth.loginHint")}
      error={error}
      onFinish={onFinish}
      submitLabel={t("auth.loginAction")}
      submitting={submitting}
    >
      <Form.Item name="login" label={t("auth.account")} rules={[{ required: true, message: t("auth.completeCredentials") }]}>
        <Input autoComplete="username" />
      </Form.Item>
      <Form.Item name="password" label={t("auth.currentPassword")} rules={[{ required: true, message: t("auth.completeCredentials") }]}>
        <Input.Password autoComplete="current-password" />
      </Form.Item>
    </AuthCard>
  );
}

export function ChangePasswordView() {
  const { t } = useTranslation();
  const { completePasswordChange } = useApp();
  const { toast } = useToast();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values: Record<string, string>) => {
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      await api("/api/auth/change-password", {
        json: { oldPassword: values.oldPassword, newPassword: values.newPassword },
      });
      completePasswordChange();
      toast(t("auth.passwordUpdated"));
    } catch (changeError) {
      setError(userErrorMessage(changeError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title={t("auth.changeTitle")}
      hint={t("auth.changeHint")}
      error={error}
      onFinish={onFinish}
      submitLabel={t("auth.saveAndEnter")}
      submitting={submitting}
    >
      <Form.Item name="oldPassword" label={t("auth.oldPassword")} rules={[{ required: true, message: t("auth.completePasswordChange") }]}>
        <Input.Password autoComplete="current-password" />
      </Form.Item>
      <Form.Item name="newPassword" label={t("auth.newPassword")} rules={[{ required: true }, { min: 8, message: t("auth.newPasswordMin") }]}>
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <Form.Item
        name="confirmPassword"
        label={t("auth.confirmNewPassword")}
        dependencies={["newPassword"]}
        rules={[
          { required: true },
          ({ getFieldValue }) => ({
            validator: (_, value) => !value || value === getFieldValue("newPassword")
              ? Promise.resolve()
              : Promise.reject(new Error(t("auth.newPasswordMismatch"))),
          }),
        ]}
      >
        <Input.Password autoComplete="new-password" />
      </Form.Item>
    </AuthCard>
  );
}
